import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, FlaskConical, Clock, CheckCircle, ShieldCheck, TrendingUp,
  Search, Filter, XCircle, RefreshCw, Calendar, User, Eye, Edit,
  Square, CheckSquare, ListChecks
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: '#f59e0b',
  completed: '#3b82f6',
  verified: '#10b981'
};

const CATEGORY_COLORS = ['#4A238F', '#0f766e', '#7c3aed', '#dc2626', '#ea580c'];

export default function LabDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  
  // Search and Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'dashboard'
  
  // Batch selection state
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [batchUpdateDialogOpen, setBatchUpdateDialogOpen] = useState(false);
  const [batchNewStatus, setBatchNewStatus] = useState('');
  const [batchUpdating, setBatchUpdating] = useState(false);
  
  const searchDebounceRef = useRef(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      // Build query parameters for filtering
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('test_category', categoryFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const [entriesRes, statsRes, patientsRes, doctorsRes] = await Promise.all([
        api.get(`/lab-entries${queryString}`),
        api.get('/lab-entries/stats/dashboard'),
        api.get('/patients'),
        api.get('/users/doctors')
      ]);
      
      setEntries(entriesRes.data || []);
      setStats(statsRes.data);
      setPatients(patientsRes.data);
      setDoctors(doctorsRes.data);
    } catch (error) {
      toast.error('Failed to fetch lab data');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, categoryFilter, priorityFilter, dateFrom, dateTo]);

  // Debounced search effect
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      fetchDashboardData();
    }, 300);
    
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery]);

  // Effect for filter changes (not search)
  useEffect(() => {
    fetchDashboardData();
  }, [statusFilter, categoryFilter, priorityFilter, dateFrom, dateTo]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setPriorityFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || categoryFilter !== 'all' || 
    priorityFilter !== 'all' || dateFrom || dateTo;

  // Batch selection handlers
  const toggleEntrySelection = (entryId) => {
    setSelectedEntries(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedEntries.length === entries.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(entries.map(e => e.id));
    }
  };

  const clearSelection = () => {
    setSelectedEntries([]);
  };

  const handleBatchStatusUpdate = async () => {
    if (!batchNewStatus || selectedEntries.length === 0) {
      toast.error('Please select entries and a status');
      return;
    }

    setBatchUpdating(true);
    try {
      const response = await api.post('/lab-entries/batch-update-status', null, {
        params: {
          entry_ids: selectedEntries,
          new_status: batchNewStatus
        },
        paramsSerializer: params => {
          const searchParams = new URLSearchParams();
          params.entry_ids.forEach(id => searchParams.append('entry_ids', id));
          searchParams.append('new_status', params.new_status);
          return searchParams.toString();
        }
      });
      
      toast.success(response.data.message);
      
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} entries failed to update`);
      }
      
      setBatchUpdateDialogOpen(false);
      setBatchNewStatus('');
      setSelectedEntries([]);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Batch update failed');
    } finally {
      setBatchUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  const statusData = stats ? [
    { name: 'Pending', value: stats.pending, color: STATUS_COLORS.pending },
    { name: 'Completed', value: stats.completed, color: STATUS_COLORS.completed },
    { name: 'Verified', value: stats.verified, color: STATUS_COLORS.verified }
  ].filter(item => item.value > 0) : [];

  const categoryData = stats?.category_breakdown ? 
    Object.entries(stats.category_breakdown).map(([category, count], index) => ({
      name: category.charAt(0).toUpperCase() + category.slice(1),
      value: count,
      color: CATEGORY_COLORS[index % CATEGORY_COLORS.length]
    })) : [];

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-amber-100 text-amber-700',
      completed: 'bg-blue-100 text-blue-700',
      verified: 'bg-emerald-100 text-emerald-700'
    };
    return variants[status] || 'bg-slate-100 text-slate-700';
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      stat: 'bg-rose-100 text-rose-700',
      urgent: 'bg-orange-100 text-orange-700',
      routine: 'bg-slate-100 text-slate-700'
    };
    return variants[priority] || 'bg-slate-100 text-slate-700';
  };

  const getCategoryBadge = (category) => {
    const variants = {
      blood: 'bg-red-100 text-red-700',
      urine: 'bg-yellow-100 text-yellow-700',
      imaging: 'bg-purple-100 text-purple-700',
      microbiology: 'bg-green-100 text-green-700',
      pathology: 'bg-pink-100 text-pink-700'
    };
    return variants[category] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div data-testid="lab-dashboard-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">
              Lab Entries
            </h1>
            <p className="text-slate-600 mt-1">Manage lab tests and view results</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex border rounded-lg overflow-hidden">
              <Button 
                variant={viewMode === 'list' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-[#4A238F]' : ''}
              >
                List View
              </Button>
              <Button 
                variant={viewMode === 'dashboard' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('dashboard')}
                className={viewMode === 'dashboard' ? 'bg-[#4A238F]' : ''}
              >
                Dashboard
              </Button>
            </div>
            <Button
              onClick={() => navigate('/lab-entries/new')}
              data-testid="create-lab-entry-button"
              className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
            >
              <Plus className="mr-2 h-4 w-4" />
              New Lab Entry
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-all">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-1">Total Entries</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total || 0}</p>
                </div>
                <div className="p-2 rounded-md bg-slate-100">
                  <FlaskConical className="h-5 w-5 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-all">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-1">Pending</p>
                  <p className="text-2xl font-bold text-amber-600">{stats?.pending || 0}</p>
                </div>
                <div className="p-2 rounded-md bg-amber-50">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-all">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-1">Completed</p>
                  <p className="text-2xl font-bold text-blue-700">{stats?.completed || 0}</p>
                </div>
                <div className="p-2 rounded-md bg-blue-100">
                  <CheckCircle className="h-5 w-5 text-blue-700" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-all">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-1">Verified</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats?.verified || 0}</p>
                </div>
                <div className="p-2 rounded-md bg-emerald-50">
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by test name, patient name, visit ID, or UHID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="lab-entries-search-input"
                />
              </div>

              {/* Filter Row */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                  </SelectContent>
                </Select>

                {/* Category Filter */}
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="blood">Blood</SelectItem>
                    <SelectItem value="urine">Urine</SelectItem>
                    <SelectItem value="imaging">Imaging</SelectItem>
                    <SelectItem value="microbiology">Microbiology</SelectItem>
                    <SelectItem value="pathology">Pathology</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>

                {/* Priority Filter */}
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="routine">Routine</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="stat">STAT</SelectItem>
                  </SelectContent>
                </Select>

                {/* Date Range */}
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-36"
                  />
                  <span className="text-gray-400">to</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-36"
                  />
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    <XCircle className="w-4 h-4 mr-1" />
                    Clear Filters
                  </Button>
                )}

                {/* Refresh */}
                <Button variant="outline" size="sm" onClick={fetchDashboardData}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
              </div>

              {/* Results count */}
              <div className="text-sm text-gray-500">
                {entries.length} entry(ies) found
                {hasActiveFilters && <span className="ml-2 text-blue-600">• Filters applied</span>}
              </div>
            </div>
          </CardContent>
        </Card>

        {viewMode === 'dashboard' ? (
          /* Charts View */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Status Distribution */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">
                  Test Status Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-slate-500 py-8">No lab entries yet</p>
                )}
              </CardContent>
            </Card>

            {/* Category Breakdown */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">
                  Test Category Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-slate-500 py-8">No lab entries yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        ) : null}

        {/* Lab Entries Table */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">
                {viewMode === 'dashboard' ? 'Recent Lab Entries' : 'All Lab Entries'}
              </CardTitle>
              
              {/* Batch Actions Bar */}
              {selectedEntries.length > 0 && (
                <div className="flex items-center gap-3 bg-purple-50 px-4 py-2 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2">
                    <CheckSquare className="w-4 h-4 text-[#4A238F]" />
                    <span className="text-sm font-medium text-[#4A238F]">
                      {selectedEntries.length} selected
                    </span>
                  </div>
                  <div className="h-4 w-px bg-purple-300" />
                  <Button
                    size="sm"
                    onClick={() => setBatchUpdateDialogOpen(true)}
                    className="bg-[#4A238F] hover:bg-[#3A1B72]"
                    data-testid="batch-update-status-btn"
                  >
                    <ListChecks className="w-4 h-4 mr-1" />
                    Update Status
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearSelection}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <div className="text-center py-12">
                <FlaskConical className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No lab entries found</p>
                {hasActiveFilters && (
                  <Button variant="link" onClick={clearFilters} className="mt-2">
                    Clear filters to see all entries
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-gray-500">
                      <th className="pb-3 pr-2">
                        <Checkbox
                          checked={selectedEntries.length === entries.length && entries.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th className="pb-3 font-medium">Test Name</th>
                      <th className="pb-3 font-medium">Patient</th>
                      <th className="pb-3 font-medium">Visit ID</th>
                      <th className="pb-3 font-medium">Category</th>
                      <th className="pb-3 font-medium">Priority</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Ordered Date</th>
                      <th className="pb-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry) => {
                      const patient = patients.find(p => p.id === entry.patient_id);
                      const doctor = doctors.find(d => d.id === entry.ordered_by);
                      const isSelected = selectedEntries.includes(entry.id);
                      
                      return (
                        <tr 
                          key={entry.id} 
                          className={`border-b hover:bg-slate-50 transition-colors ${isSelected ? 'bg-purple-50' : ''}`}
                          data-testid={`lab-entry-row-${entry.id}`}
                        >
                          <td className="py-3 pr-2">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleEntrySelection(entry.id)}
                              data-testid={`select-entry-${entry.id}`}
                            />
                          </td>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <FlaskConical className="w-4 h-4 text-[#4A238F]" />
                              <span className="font-medium">{entry.test_name}</span>
                            </div>
                          </td>
                          <td className="py-3">
                            <div>
                              <p className="font-medium text-slate-900">
                                {entry.patient_name || patient?.full_name || 'Unknown'}
                              </p>
                              <p className="text-xs text-slate-500">
                                {entry.patient_number || patient?.patient_number || ''}
                              </p>
                            </div>
                          </td>
                          <td className="py-3">
                            <span className="font-mono text-sm text-slate-600">
                              {entry.visit_id?.slice(0, 8) || '-'}...
                            </span>
                          </td>
                          <td className="py-3">
                            <Badge className={getCategoryBadge(entry.test_category)}>
                              {entry.test_category}
                            </Badge>
                          </td>
                          <td className="py-3">
                            <Badge className={getPriorityBadge(entry.priority)}>
                              {entry.priority === 'stat' ? 'STAT' : entry.priority}
                            </Badge>
                          </td>
                          <td className="py-3">
                            <Badge className={getStatusBadge(entry.status)}>
                              {entry.status}
                            </Badge>
                          </td>
                          <td className="py-3 text-sm text-slate-600">
                            {entry.ordered_date ? new Date(entry.ordered_date).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-3">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/lab-entries/${entry.id}`)}
                                title="View/Edit"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Batch Update Status Dialog */}
        <Dialog open={batchUpdateDialogOpen} onOpenChange={setBatchUpdateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-[#4A238F]" />
                Batch Update Status
              </DialogTitle>
              <DialogDescription>
                Update the status of {selectedEntries.length} selected lab entries at once.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm text-[#4A238F] font-medium">
                  {selectedEntries.length} entries selected
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  This action will update all selected lab entries to the same status.
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">New Status</label>
                <Select value={batchNewStatus} onValueChange={setBatchNewStatus}>
                  <SelectTrigger data-testid="batch-status-select">
                    <SelectValue placeholder="Select new status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-600" />
                        Pending
                      </div>
                    </SelectItem>
                    <SelectItem value="completed">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600" />
                        Completed
                      </div>
                    </SelectItem>
                    <SelectItem value="verified">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-600" />
                        Verified
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setBatchUpdateDialogOpen(false);
                  setBatchNewStatus('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBatchStatusUpdate}
                disabled={!batchNewStatus || batchUpdating}
                className="bg-[#4A238F] hover:bg-[#3A1B72]"
                data-testid="confirm-batch-update-btn"
              >
                {batchUpdating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Update {selectedEntries.length} Entries
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
